import {useCallback, useState, useMemo, useEffect, useRef} from 'react';

import {
    FIXED_SCREENS,
    YCLIENTS_RECORD_TYPE,
    YClientsFormContactFields,
    YClientsFormFooterState,
    YClientsFormState,
} from '@target-web/leadads-form-view';

import {YClientsDataType} from 'api/types/LeadForm';
import {useContactField} from './useContactField';
import {apiUrl, CODE_LENGTH, headers, isDev} from 'utils/constants';
import {getFirstYClientsScreen} from './useNavigation';
import {useUserInfo} from 'utils/userInfoContext';
import {useSearchParams} from 'utils/searchParamsContext';
import {validatorByField} from 'utils/validation';
import usePhoneVerificationYClientsController from './usePhoneVerificationYClientsController';
import {SendLeadYClientsResponse} from 'utils/types';

export interface LeadFormControllerProps {
    leadForm: YClientsDataType;
    currentPage: FIXED_SCREENS;
    hasDateTimeOnPrevious: boolean;
    goToPage: (id: FIXED_SCREENS) => void;
    initialServiceId: number | null;
}

const steps = ['master', 'service', 'datetime'] as const;

const stepsMap = {
    master: FIXED_SCREENS.YCLIENTS_MASTER,
    service: FIXED_SCREENS.YCLIENTS_SERVICE,
    datetime: FIXED_SCREENS.YCLIENTS_DATETIME,
};

const textPageMap = {
    [FIXED_SCREENS.YCLIENTS_MASTER]: 'Выбрать специалиста',
    [FIXED_SCREENS.YCLIENTS_SERVICE]: 'Выбрать услуги',
    [FIXED_SCREENS.YCLIENTS_DATETIME]: 'Выбрать дату и время',
};

const initialData = {
    staffId: null,
    datetime: '',
    services: [],
    activity: null,
    activityRecorsCount: 1,
};

const initialQuickWriteData = {
    staffId: null,
    datetime: '',
    services: [],
};

const chenkResponseFn = async (res: Response): Promise<SendLeadYClientsResponse> => {
    if (res.status < 500) {
        // наша прокся не упала
        // TODO доработать ошибочки
        const parsedRes = await res.json();
        if (parsedRes.http_status_code) {
            // пришел статус от yclients
            return {
                http_status_code: parsedRes.http_status_code,
                status: parsedRes.data.success,
                message: parsedRes.data?.meta?.message,
            };
            // пока просто решаем куда кидать дальше, но надо доработать ошибки
        }
        // статус не пришел, значит упали на проксе
        return false;
    }

    return false;
};

export function useYClientsFormController({
    leadForm,
    currentPage,
    hasDateTimeOnPrevious,
    goToPage,
    initialServiceId,
}: LeadFormControllerProps) {
    const [activeMaster, setActiveMaster] = useState<number | null>(null); // используется для карточки мастера
    const {formDataHash} = useSearchParams();
    const [dataState, setFormState] = useState<Omit<YClientsFormState, 'contacts' | 'firstScreen'>>(
        JSON.parse(
            JSON.stringify({
                ...initialData,
                ...(initialServiceId ? {services: [initialServiceId]} : {}),
            }),
        ),
    );
    const [dataQuickWriteState, setQuickWriteFormState] = useState<YClientsFormState['quickWrite']>(
        JSON.parse(JSON.stringify(initialQuickWriteData)),
    );

    const fullname = useContactField<string>('', validatorByField['fullname']);
    const phone = useContactField<string>('', validatorByField['phone']);
    const email = useContactField<string>('', validatorByField['email']);
    const comment = useContactField<string>('');

    const userInfo = useUserInfo();

    useEffect(() => {
        if (userInfo.general && userInfo.general.first_name) {
            fullname.onChange(userInfo.general.first_name);
        }
        if (userInfo.phone && userInfo.phone_hash) {
            phone.onChange(userInfo.phone, userInfo.phone_hash);
        }
        if (userInfo.email && userInfo.email_hash) {
            email.onChange(userInfo.email, userInfo.email_hash);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo]);

    const formState = useMemo<YClientsFormState>(
        () => ({
            ...dataState,
            quickWrite: dataQuickWriteState,
            firstScreen: getFirstYClientsScreen(leadForm.data, {initialServiceId}),
            contacts: {
                fullname,
                phone,
                email,
                comment,
            },
        }),
        [dataState, fullname, phone, email, comment, dataQuickWriteState, leadForm.data, initialServiceId],
    );

    const resetDataState = useCallback(() => {
        setFormState(JSON.parse(JSON.stringify(initialData)));
    }, []);

    const hasErrors = useCallback(() => {
        return (Object.keys(formState.contacts) as unknown as YClientsFormContactFields[]).some((k) => {
            return formState.contacts[k].validate();
        });
    }, [formState.contacts]);

    const sendLead = useCallback(
        async (isQuickWrite = false, code?: string): Promise<SendLeadYClientsResponse> => {
            if (hasErrors()) return false;

            const commonParams = {
                contact_info: {
                    first_name: fullname.value,
                    ...(email.value ? {email: email.value} : {}),
                    phone: phone.isFilled
                        ? phone.value.startsWith('+')
                            ? phone.value
                            : `+${phone.value}`
                        : `+7${phone.value}`,
                    ...(phone.isFilled ? {phone_hash: phone.hash} : {}),
                    ...(email.isFilled ? {email_hash: email.hash} : {}),
                },
                ...(userInfo.email_data ? {email_data: userInfo.email_data} : {}),
                ...(userInfo.phone_data ? {phone_data: userInfo.phone_data} : {}),
            };

            // записываемся в групповое
            if (formState.activity) {
                const bookData = {
                    ...commonParams,
                    yc_body: {
                        comment: comment.value,
                        clients_count: formState.activityRecorsCount,
                    },
                };
                return fetch(`${apiUrl}/yclients/activity/${formState.activity}/book?lead_form_data=${formDataHash}`, {
                    headers,
                    method: 'POST',
                    body: JSON.stringify(bookData),
                })
                    .then(chenkResponseFn)
                    .catch((_e) => {
                        // TODO добавить обработку ошибок сети
                        return false;
                    });
            }

            // записываемся на индивидуальное
            // проверяем доступность
            const checkResult = await fetch(`${apiUrl}/yclients/book_check?lead_form_data=${formDataHash}`, {
                headers,
                method: 'POST',
                body: JSON.stringify({
                    appointments: [
                        {
                            id: 1, // это уникальная айдишка, при ошибке записи вернется в ключе ошибки
                            services: isQuickWrite ? formState.quickWrite.services : formState.services,
                            staff_id: isQuickWrite ? formState.quickWrite.staffId : formState.staffId,
                            datetime: isQuickWrite ? formState.quickWrite.datetime : formState.datetime,
                        },
                    ],
                }),
            })
                .then(chenkResponseFn)
                .catch((_e) => {
                    // TODO добавить обработку ошибок сети
                    return false;
                });

            // TODO проверка о том что места уже забронированы
            if (!checkResult) return false;

            // все ок, записываем
            return fetch(`${apiUrl}/yclients/book_record?lead_form_data=${formDataHash}`, {
                headers,
                method: 'POST',
                body: JSON.stringify({
                    ...commonParams,
                    yc_body: {
                        ...(code ? {code} : {}),
                        comment: isQuickWrite
                            ? `Перезвонить! Клиент из короткой формы vk рекламы${
                                  comment.value ? `; Комментарий клиента: ${comment.value}` : ''
                              }`
                            : comment.value,
                        appointments: [
                            {
                                id: 1,
                                services: isQuickWrite ? formState.quickWrite.services : formState.services,
                                staff_id: isQuickWrite ? formState.quickWrite.staffId : formState.staffId,
                                datetime: isQuickWrite ? formState.quickWrite.datetime : formState.datetime,
                            },
                        ],
                    },
                }),
            })
                .then(chenkResponseFn)
                .catch((_e) => {
                    // TODO добавить обработку ошибок сети
                    return false;
                });
        },
        [
            formState.activity,
            formState.quickWrite.services,
            formState.quickWrite.staffId,
            formState.quickWrite.datetime,
            formState.services,
            formState.staffId,
            formState.datetime,
            formState.activityRecorsCount,
            fullname.value,
            email.value,
            email.isFilled,
            email.hash,
            phone.isFilled,
            phone.value,
            phone.hash,
            userInfo?.email_data,
            userInfo?.phone_data,
            formDataHash,
            comment.value,
            hasErrors,
        ],
    );

    const isQuickWriteFlow = useRef(false);

    const sendLeadWithCode = useCallback(
        async (code: string) => {
            const res = await sendLead(isQuickWriteFlow.current, code);
            if (res && res.status) {
                // если смогли записаться - кидаем на финальный экран
                goToPage(
                    isQuickWriteFlow.current
                        ? FIXED_SCREENS.YCLIENTS_QUICK_WRITE_SUCCESS
                        : FIXED_SCREENS.YCLIENTS_SUCCESS,
                );
            }
            return res;
        },
        [sendLead, goToPage],
    );

    const phoneVerificationController = usePhoneVerificationYClientsController(CODE_LENGTH, sendLeadWithCode);

    const onSignUp = useCallback(
        async (isQuickWrite: boolean, nextPage: FIXED_SCREENS) => {
            if (hasErrors()) return;

            // если требуется код - то сетим номер из стора и переходим на страницу верификации
            if (leadForm.data.form.phone_confirmation) {
                phoneVerificationController.setPhoneForCheckingVerificationStatus({
                    value: phone.isFilled ? phone.value : '7' + phone.value,
                    ...(phone.isFilled ? {hash: userInfo.phone_hash} : {}),
                });
                return goToPage(FIXED_SCREENS.YCLIENTS_PHONE_VERIFICATION);
            }

            // иначе записываем без проверки номера
            const res = await sendLead(isQuickWrite);
            if (res && res.status) {
                return goToPage(nextPage);
            }
            return goToPage(FIXED_SCREENS.YCLIENTS_SIGNIN_ERROR);
        },
        [
            goToPage,
            leadForm.data.form.phone_confirmation,
            phone.isFilled,
            phone.value,
            phoneVerificationController,
            sendLead,
            userInfo.phone_hash,
            hasErrors,
        ],
    );

    const getNextStep = useCallback(
        (curStep: FIXED_SCREENS) => {
            // отсортированные скрины с мастером/услугой/датой
            const menuSteps = leadForm.data.form.steps
                .filter((s) => steps.includes(s.step))
                .sort((a, b) => a.num - b.num)
                .map((s) => stepsMap[s.step]);
            const curIndex = menuSteps.findIndex((s) => s === curStep);
            return menuSteps[curIndex + 1] || menuSteps[0]; // если переходим с _MIX - то выставляем первый шаг
        },
        [leadForm.data.form.steps],
    );

    const footerState = useMemo<YClientsFormFooterState>(() => {
        let text = '';
        let nextPage = FIXED_SCREENS.YCLIENTS_CHECK;
        let onClickFooterBtn = () => {
            goToPage(nextPage);
        };

        if (
            [FIXED_SCREENS.YCLIENTS_MASTER, FIXED_SCREENS.YCLIENTS_SERVICE, FIXED_SCREENS.YCLIENTS_DATETIME].includes(
                currentPage,
            )
        ) {
            // если все заполнено
            if (formState.staffId && formState.services.length && formState.datetime) {
                text = 'Готово';
                nextPage = FIXED_SCREENS.YCLIENTS_CHECK;
            } else {
                if (currentPage === FIXED_SCREENS.YCLIENTS_MASTER && formState.staffId) {
                    // если пошаговый - то ищем следующий шаг
                    if (leadForm.data.form.sequence_type !== 'menu') {
                        nextPage = getNextStep(currentPage);
                    } else if (!formState.services.length) {
                        nextPage = FIXED_SCREENS.YCLIENTS_SERVICE;
                    } else if (!formState.datetime) {
                        nextPage = FIXED_SCREENS.YCLIENTS_DATETIME;
                    }
                }

                if (currentPage === FIXED_SCREENS.YCLIENTS_SERVICE && formState.services.length) {
                    if (leadForm.data.form.sequence_type !== 'menu') {
                        nextPage = getNextStep(currentPage);
                    } else if (!formState.datetime) {
                        nextPage = FIXED_SCREENS.YCLIENTS_DATETIME;
                    } else if (!formState.staffId) {
                        nextPage = FIXED_SCREENS.YCLIENTS_MASTER;
                    }
                }

                if (currentPage === FIXED_SCREENS.YCLIENTS_DATETIME && formState.datetime) {
                    if (leadForm.data.form.sequence_type !== 'menu') {
                        nextPage = getNextStep(currentPage);
                    } else if (!formState.staffId) {
                        nextPage = FIXED_SCREENS.YCLIENTS_MASTER;
                    } else if (!formState.services.length) {
                        nextPage = FIXED_SCREENS.YCLIENTS_SERVICE;
                    }
                }
                // @ts-ignore // TODO
                text = textPageMap[nextPage];
            }
        }

        if (currentPage === FIXED_SCREENS.YCLIENTS_MIX) {
            if (leadForm.data.form.sequence_type === 'menu') {
                nextPage = FIXED_SCREENS.YCLIENTS_INDIVIDUAL;
            } else {
                nextPage = getNextStep(currentPage);
            }
        }

        if (currentPage === FIXED_SCREENS.YCLIENTS_ACTIVITY) {
            text = 'Продолжить';
            nextPage = FIXED_SCREENS.YCLIENTS_CHECK;
        }

        if (currentPage === FIXED_SCREENS.YCLIENTS_QUICK_WRITE) {
            isQuickWriteFlow.current = true; // если попали на эту страницу - значит идем по короткому флоу
            text = 'Оставить заявку';
            nextPage = FIXED_SCREENS.YCLIENTS_QUICK_WRITE_SUCCESS;
            onClickFooterBtn = () => {
                onSignUp(true, nextPage);
            };
        }

        if (currentPage === FIXED_SCREENS.YCLIENTS_CHECK) {
            isQuickWriteFlow.current = false; // страница проверки записи идет только на стандартном флоу
            text = 'Записаться';
            nextPage = FIXED_SCREENS.YCLIENTS_SUCCESS;
            onClickFooterBtn = () => {
                onSignUp(false, nextPage);
            };
        }

        return {
            isShow: !!text,
            text,
            onClick: onClickFooterBtn,
            ...(currentPage === FIXED_SCREENS.YCLIENTS_QUICK_WRITE
                ? {
                      goToChoose: () => {
                          goToPage(
                              leadForm.data.company.record_type_id === YCLIENTS_RECORD_TYPE.mix
                                  ? FIXED_SCREENS.YCLIENTS_MIX
                                  : FIXED_SCREENS.YCLIENTS_INDIVIDUAL,
                          );
                      },
                  }
                : {}),
        };
    }, [
        formState,
        currentPage,
        goToPage,
        leadForm.data.form.sequence_type,
        leadForm.data.company.record_type_id,
        getNextStep,
        onSignUp,
    ]);

    const setStaffId = useCallback(
        (staffId: number | null) => {
            setFormState((s) => ({
                ...s,
                staffId,
                datetime:
                    // TODO потенциальное место для бага, упростить
                    leadForm.data.form.sequence_type === 'menu' && !hasDateTimeOnPrevious
                        ? s.staffId !== staffId
                            ? ''
                            : s.datetime
                        : s.datetime,
            }));
        },
        [leadForm.data.form.sequence_type, hasDateTimeOnPrevious],
    );

    const setDateTime = useCallback((datetime: string, staffId?: number) => {
        // может выставлять как со страницы выбора даты, так и из карточек мастеров
        setFormState((s) => {
            if (staffId === undefined) {
                // пришла только дата, ее и сетим
                return {
                    ...s,
                    datetime,
                };
            }

            let newDateTime = '';
            if (s.staffId !== staffId) {
                // переключили мастера
                newDateTime = datetime;
            } else {
                // тот же мастер, но либо изменили время, либо кликнули по активному
                if (s.datetime !== datetime) {
                    // изменили время - сетим новое
                    newDateTime = datetime;
                }
            }
            return {
                ...s,
                datetime: newDateTime,
                staffId,
            };
        });
    }, []);

    const resetDateTime = useCallback(() => {
        setFormState((s) => ({
            ...s,
            datetime: initialData.datetime,
        }));
    }, []);

    const toggleService = useCallback(
        (serviceId: number) => {
            if (formState.services.includes(serviceId)) {
                setFormState((s) => ({
                    ...s,
                    services: formState.services.filter((id) => id !== serviceId),
                }));
            } else {
                setFormState((s) => ({
                    ...s,
                    services: [...formState.services, serviceId],
                }));
            }
        },
        [formState.services],
    );

    const setServices = useCallback((services: number[]) => {
        setFormState((s) => ({
            ...s,
            services,
        }));
    }, []);

    const resetServices = useCallback(() => {
        setFormState((s) => ({
            ...s,
            services: [],
        }));
    }, []);

    const setActivity = useCallback((id: number) => {
        setFormState((s) => ({
            ...s,
            activity: id,
        }));
    }, []);

    const resetActivity = useCallback(() => {
        setFormState((s) => ({
            ...s,
            activity: null,
            activityRecorsCount: 1,
        }));
    }, []);

    const plusActivityRecordsCount = useCallback(() => {
        setFormState((s) => ({
            ...s,
            activityRecorsCount: s.activityRecorsCount + 1,
        }));
    }, []);

    const minusActivityRecordsCount = useCallback(() => {
        setFormState((s) => ({
            ...s,
            activityRecorsCount: s.activityRecorsCount - 1,
        }));
    }, []);

    useEffect(() => {
        if (isDev) {
            console.log('Form State: ', formState);
        }
    }, [formState]);

    return {
        phoneVerificationController,
        formState,
        footerState,
        activeMaster,
        setActiveMaster,
        setStaffId,
        toggleService,
        setServices,
        resetServices,
        setDateTime,
        setActivity,
        resetDateTime,
        resetActivity,
        getNextStep,
        resetDataState,
        plusActivityRecordsCount,
        minusActivityRecordsCount,
        setQuickWriteState: setQuickWriteFormState,
    };
}
